import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Button from "../../components/Kids/Button";
import StatCard from "../../components/Kids/StatCard";
import Pill from "../../components/Kids/Pill";
import Skeleton from "../../components/Kids/Skeleton";

const API = process.env.REACT_APP_API_URL;
const FF = "'Fredoka',sans-serif"; // display font

/* Load the YouTube IFrame API once. */
function loadYT() {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { if (prev) prev(); resolve(window.YT); };
    if (!document.getElementById("yt-iframe-api")) {
      const s = document.createElement("script");
      s.id = "yt-iframe-api";
      s.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(s);
    }
  });
}

/* Returns false if the video owner disabled embedding (error 101/150) or the
   video is missing/private — i.e. it would NOT play inside Cabil.
   Resolves `true` (assume OK, don't block the admin) if the check itself
   can't run at all — e.g. an ad-blocker/privacy extension blocks the
   youtube.com/iframe_api script. Without this, loadYT()'s promise would
   never resolve, so the 4s "no error" fallback below (which only fires
   *inside* that .then()) would never get scheduled either — leaving the
   Add button stuck on "Checking…" forever even though the video had
   already been saved by the earlier POST. */
function testEmbeddable(video) {
  const check = new Promise((resolve) => {
    loadYT().then((YT) => {
      const div = document.createElement("div");
      div.style.cssText = "position:fixed;left:-9999px;top:-9999px;width:320px;height:180px";
      document.body.appendChild(div);
      let done = false;
      let player;
      const finish = (ok) => {
        if (done) return; done = true;
        try { player && player.destroy && player.destroy(); } catch { /* ignore */ }
        div.remove();
        resolve(ok);
      };
      const playerVars = video.type === "playlist"
        ? { listType: "playlist", list: video.youtube_id, origin: window.location.origin }
        : { origin: window.location.origin };
      player = new YT.Player(div, {
        videoId: video.type === "video" ? video.youtube_id : undefined,
        playerVars,
        events: { onError: () => finish(false) },
      });
      // No error within 4s → it embeds fine.
      setTimeout(() => finish(true), 4000);
    });
  });

  const timeout = new Promise((resolve) => setTimeout(() => resolve(true), 6000));
  return Promise.race([check, timeout]);
}

export default function KidsWatchAdmin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token    = localStorage.getItem("kids_parent_token");
  const auth     = { headers: { Authorization: `Bearer ${token}` } };

  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ enabled: false, minutes_per_game: 10, daily_cap: 60 });
  const [savingSettings, setSavingSettings] = useState(false);

  const [videos, setVideos] = useState([]);
  const [newVid, setNewVid] = useState({ title: "", url: "" });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!token) { navigate("/kids/login"); return; }
    Promise.all([
      axios.get(`${API}/api/kids/watch/settings`, auth),
      axios.get(`${API}/api/kids/watch/videos`, auth),
    ]).then(([s, v]) => {
      setSettings(s.data.data);
      setVideos(v.data.data || []);
      setLoading(false);
    }).catch(() => { toast.error("Couldn't load watch settings."); navigate("/kids/parent/dashboard"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.post(`${API}/api/kids/watch/settings`, {
        enabled: !!settings.enabled,
        minutes_per_game: Number(settings.minutes_per_game) || 10,
        daily_cap: Number(settings.daily_cap) || 60,
      }, auth);
      toast.success("Watch settings saved! 📺");
    } catch (e) {
      toast.error(e.response?.data?.message || "Couldn't save.");
    } finally { setSavingSettings(false); }
  };

  const addVideo = async (e) => {
    e.preventDefault();
    if (!newVid.title.trim() || !newVid.url.trim()) return toast.warn("Add a title and a YouTube link.");
    setAdding(true);
    try {
      const res = await axios.post(`${API}/api/kids/watch/videos`, newVid, auth);
      const saved = res.data.data;

      // Make sure it will actually play for kids (some owners disable embedding).
      const ok = await testEmbeddable(saved);
      if (!ok) {
        try { await axios.delete(`${API}/api/kids/watch/videos/${saved.id}`, auth); } catch { /* ignore */ }
        toast.error("That video's owner disabled embedding, so it won't play in Cabil. Try another video or a playlist.");
        return;
      }

      setVideos(v => [saved, ...v]);
      setNewVid({ title: "", url: "" });
      toast.success("Added & checked — it plays! ✅");
    } catch (e) {
      toast.error(e.response?.data?.message || "Couldn't add that link.");
    } finally { setAdding(false); }
  };

  const removeVideo = async (id) => {
    if (!window.confirm("Remove this from the approved list?")) return;
    try {
      await axios.delete(`${API}/api/kids/watch/videos/${id}`, auth);
      setVideos(v => v.filter(x => x.id !== id));
    } catch { toast.error("Couldn't remove."); }
  };

  if (loading) return (
    <div className="min-h-screen" style={{ background: "linear-gradient(150deg,#FFF6EE,#FFF9F4 55%,#fff)" }}>
      <header className="bg-white shadow-card px-6 py-4 flex items-center justify-between">
        <Skeleton className="h-7 w-36" rounded="10px" />
        <Skeleton className="h-5 w-32" rounded="10px" />
      </header>
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        <div className="space-y-2">
          <Skeleton className="h-7 w-56" rounded="10px" />
          <Skeleton className="h-4 w-full max-w-md" rounded="8px" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Skeleton className="h-24" rounded="16px" />
          <Skeleton className="h-24" rounded="16px" />
          <Skeleton className="h-24" rounded="16px" />
        </div>
        <div className="rounded-card bg-white border border-cardline shadow-card p-6 space-y-4">
          <Skeleton className="h-5 w-40" rounded="8px" />
          <Skeleton className="h-11 w-full" rounded="12px" />
          <div className="grid sm:grid-cols-2 gap-4">
            <Skeleton className="h-16" rounded="12px" />
            <Skeleton className="h-16" rounded="12px" />
          </div>
        </div>
        <div className="rounded-card bg-white border border-cardline shadow-card p-6 space-y-4">
          <Skeleton className="h-5 w-56" rounded="8px" />
          <Skeleton className="h-11 w-full" rounded="12px" />
          <Skeleton className="h-16 w-full" rounded="12px" />
          <Skeleton className="h-16 w-full" rounded="12px" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(150deg,#FFF6EE,#FFF9F4 55%,#fff)" }}>
      <header className="bg-white shadow-card px-6 py-4 flex items-center justify-between">
        <Link to="/kids/parent/dashboard" className="flex items-center gap-3">
          <span className="text-2xl">⭐</span>
          <span className="font-display font-bold text-brand text-xl">Cabil <span className="text-gold">Kids</span></span>
        </Link>
        <Link to="/kids/parent/dashboard" className="text-faint hover:text-brand text-sm font-body font-bold transition">← Back to dashboard</Link>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        <div>
          <h1 className="text-2xl text-ink" style={{ fontFamily: FF, fontWeight: 700 }}>📺 {t("kids.watchAdmin.title")}</h1>
          <p className="text-muted text-sm mt-1 font-body">
            Kids earn YouTube watch minutes by finishing learning games. They can only watch the videos you approve below.
          </p>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-3">
          <StatCard emoji={settings.enabled ? "✅" : "⏸️"} value={settings.enabled ? "On" : "Off"} label={t("kids.watchAdmin.rewardsOn")} />
          <StatCard emoji="⏱️" value={settings.minutes_per_game} label={t("kids.watchAdmin.minutesPerGame")} />
          <StatCard emoji="📺" value={videos.length} label={t("kids.watchAdmin.approved")} />
        </div>

        {/* Settings */}
        <section className="bg-white rounded-card border border-cardline shadow-card p-6">
          <h2 className="font-display font-bold text-ink text-lg mb-4">Reward settings</h2>

          <label className="flex items-center gap-3 mb-5">
            <input type="checkbox" checked={!!settings.enabled}
              onChange={e => setSettings(s => ({ ...s, enabled: e.target.checked }))}
              className="w-5 h-5 accent-brand" />
            <span className="font-body font-bold text-ink">Enable watch rewards for my kids</span>
          </label>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-body font-bold text-muted mb-1">Minutes earned per game</label>
              <input type="number" value={settings.minutes_per_game}
                onChange={e => setSettings(s => ({ ...s, minutes_per_game: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-cardline focus:border-brand outline-none font-body" />
              <p className="text-xs text-faint mt-1 font-body">Each completed learning game gives this many minutes.</p>
            </div>
            <div>
              <label className="block text-sm font-body font-bold text-muted mb-1">Maximum saved minutes</label>
              <input type="number" value={settings.daily_cap}
                onChange={e => setSettings(s => ({ ...s, daily_cap: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-cardline focus:border-brand outline-none font-body" />
              <p className="text-xs text-faint mt-1 font-body">Their balance can't grow past this — they must keep learning to refill.</p>
            </div>
          </div>

          <Button onClick={saveSettings} disabled={savingSettings} className="mt-5">
            {savingSettings ? "Saving…" : t("kids.watchAdmin.saveSettings")}
          </Button>
        </section>

        {/* Approved videos */}
        <section className="bg-white rounded-card border border-cardline shadow-card p-6">
          <h2 className="font-display font-bold text-ink text-lg mb-1">Approved videos & playlists</h2>
          <p className="text-muted text-sm mb-4 font-body">Paste a YouTube video or playlist link. Kids can only watch what's on this list — and nothing else. We check each link plays before adding it (some owners disable embedding; those are rejected).</p>

          <form onSubmit={addVideo} className="grid sm:grid-cols-[1fr_1.5fr_auto] gap-3 mb-5">
            <input value={newVid.title} onChange={e => setNewVid(v => ({ ...v, title: e.target.value }))}
              placeholder="Title (e.g. Counting Songs)"
              className="px-4 py-2.5 rounded-xl border border-cardline focus:border-brand outline-none font-body" />
            <input value={newVid.url} onChange={e => setNewVid(v => ({ ...v, url: e.target.value }))}
              placeholder="https://youtube.com/watch?v=…  or  …playlist?list=…"
              className="px-4 py-2.5 rounded-xl border border-cardline focus:border-brand outline-none font-body" />
            <Button type="submit" disabled={adding} className="whitespace-nowrap">
              {adding ? "Checking…" : t("kids.watchAdmin.addVideo")}
            </Button>
          </form>

          {videos.length === 0 ? (
            <p className="text-sm text-faint font-body">No approved videos yet. Add one above so your kids have something to watch.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {videos.map(v => (
                <div key={v.id} className="flex items-center gap-3 border border-cardline rounded-xl p-2">
                  <div className="w-20 h-12 rounded-lg bg-cream overflow-hidden flex items-center justify-center flex-shrink-0">
                    {v.thumbnail
                      ? <img src={v.thumbnail} alt="" className="w-full h-full object-cover" />
                      : <span className="text-xl">▶️</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-body font-bold text-ink text-sm truncate">{v.title}</div>
                    <Pill className="mt-1">{v.type === "playlist" ? "Playlist" : "Video"}</Pill>
                  </div>
                  <button onClick={() => removeVideo(v.id)}
                    className="text-danger hover:opacity-70 text-sm font-body font-bold px-2 transition">{t("kids.watchAdmin.remove")}</button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
